import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Save, Upload, FileCheck, X, Camera } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function ReviewManager({ departments, selectedDept, setSelectedDept, tenantId }) {
  const [kras, setKras] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [month, setMonth] = useState('');
  const [year, setYear] = useState(new Date().getFullYear());
  const [kpiReviews, setKpiReviews] = useState([]);
  const [overallFeedback, setOverallFeedback] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (selectedDept) {
      fetchKRAsAndKPIs();
    }
  }, [selectedDept]);

  useEffect(() => {
    if (kpis.length > 0) {
      // Initialize KPI reviews with default values
      setKpiReviews(
        kpis.map((kpi) => ({
          kpi_id: kpi.id,
          achieved_percentage: 0,
          feedback: '',
          evidence_file: null,  // Store file info
          evidence_uploading: false
        }))
      );
    }
  }, [kpis]);

  const fetchKRAsAndKPIs = async () => {
    try {
      const krasUrl = tenantId 
        ? `${API}/kras/${selectedDept}?tenant_id=${tenantId}`
        : `${API}/kras/${selectedDept}`;
      const kpisUrl = tenantId 
        ? `${API}/kpis/department/${selectedDept}?tenant_id=${tenantId}`
        : `${API}/kpis/department/${selectedDept}`;
      
      const [krasRes, kpisRes] = await Promise.all([
        axios.get(krasUrl),
        axios.get(kpisUrl)
      ]);
      setKras(krasRes.data);
      setKpis(kpisRes.data);
    } catch (error) {
      console.error('Error fetching KRAs/KPIs:', error);
      toast.error('Failed to load KRAs/KPIs');
    }
  };

  const updateKPIReview = (kpiId, field, value) => {
    setKpiReviews(prev =>
      prev.map(review =>
        review.kpi_id === kpiId
          ? { ...review, [field]: value }
          : review
      )
    );
  };

  const handleFileUpload = async (kpiId, file) => {
    // Validate file size (5MB)
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      toast.error(`File size exceeds 5MB limit. Current size: ${(file.size / 1024 / 1024).toFixed(2)}MB`);
      // Reset file input
      if (fileInputRefs.current[kpiId]) {
        fileInputRefs.current[kpiId].value = '';
      }
      return;
    }

    // Validate file extension
    const allowedExtensions = ['.pdf', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.gif'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedExtensions.includes(fileExt)) {
      toast.error(`File type not allowed. Allowed types: PDF, XLS, XLSX, JPG, PNG, GIF`);
      // Reset file input
      if (fileInputRefs.current[kpiId]) {
        fileInputRefs.current[kpiId].value = '';
      }
      return;
    }

    // Set uploading state
    updateKPIReview(kpiId, 'evidence_uploading', true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload-evidence`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        updateKPIReview(kpiId, 'evidence_file', {
          filename: response.data.filename,
          original_filename: response.data.original_filename,
          size: response.data.size,
          file_type: response.data.file_type
        });
        toast.success('Evidence uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload evidence');
      // Reset file input on error
      if (fileInputRefs.current[kpiId]) {
        fileInputRefs.current[kpiId].value = '';
      }
    } finally {
      updateKPIReview(kpiId, 'evidence_uploading', false);
    }
  };

  const removeEvidence = (kpiId) => {
    updateKPIReview(kpiId, 'evidence_file', null);
    // Reset file input
    if (fileInputRefs.current[kpiId]) {
      fileInputRefs.current[kpiId].value = '';
    }
    toast.success('Evidence removed');
  };

  const handleCameraCapture = async (kpiId) => {
    try {
      // Create a hidden file input for camera capture
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.capture = 'environment'; // Use rear camera on mobile devices
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (file) {
          // Validate file type
          const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
          if (!validTypes.includes(file.type)) {
            toast.error('Please capture a valid image (JPG, PNG, or GIF)');
            return;
          }
          
          // Validate file size (5MB)
          if (file.size > 5 * 1024 * 1024) {
            toast.error('Image size must be less than 5MB');
            return;
          }
          
          // Upload the captured photo
          await handleFileUpload(kpiId, file);
        }
      };
      
      // Trigger the camera
      input.click();
    } catch (error) {
      console.error('Error capturing photo:', error);
      toast.error('Failed to access camera');
    }
  };

  const handleSubmitReview = async () => {
    if (!month || !year) {
      toast.error('Please select month and year');
      return;
    }

    // Validate all KPIs have been reviewed
    const unreviewed = kpiReviews.filter(r => r.achieved_percentage === 0 || r.achieved_percentage === '');
    if (unreviewed.length > 0) {
      toast.error('Please provide achieved score for all KPIs');
      return;
    }

    // Validate scores don't exceed weightage
    const invalidScores = kpiReviews.filter((r) => {
      const kpi = kpis.find(k => k.id === r.kpi_id);
      if (!kpi) {
        console.error('KPI not found for review:', r.kpi_id);
        return true;
      }
      return parseFloat(r.achieved_percentage) < 0 || parseFloat(r.achieved_percentage) > kpi.weightage;
    });
    if (invalidScores.length > 0) {
      toast.error('Achieved score must be between 0 and the KPI weightage');
      return;
    }

    setLoading(true);
    try {
      const url = tenantId 
        ? `${API}/reviews?tenant_id=${tenantId}`
        : `${API}/reviews`;
      
      await axios.post(url, {
        department_id: selectedDept,
        month,
        year,
        kpi_reviews: kpiReviews.map(r => ({
          kpi_id: r.kpi_id,
          achieved_percentage: parseFloat(r.achieved_percentage),
          feedback: r.feedback || '',
          evidence_file: r.evidence_file || null
        })),
        overall_feedback: overallFeedback
      });
      
      toast.success('Review submitted successfully');
      
      // Reset form
      setKpiReviews(
        kpis.map((kpi) => ({
          kpi_id: kpi.id,
          achieved_percentage: 0,
          feedback: '',
          evidence_file: null,
          evidence_uploading: false
        }))
      );
      setOverallFeedback('');
      
      // Reset all file inputs
      Object.keys(fileInputRefs.current).forEach(kpiId => {
        if (fileInputRefs.current[kpiId]) {
          fileInputRefs.current[kpiId].value = '';
        }
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setLoading(false);
    }
  };

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <div className="space-y-6">
      <Card className="p-6 bg-white shadow-lg">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-slate-900">Conduct Monthly Review</h2>
          <Select value={selectedDept || ''} onValueChange={setSelectedDept}>
            <SelectTrigger className="w-64" data-testid="dept-select-review">
              <SelectValue placeholder="Select Department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {!selectedDept ? (
          <p className="text-center text-slate-500 py-12">Select a department to conduct review</p>
        ) : kpis.length === 0 ? (
          <p className="text-center text-slate-500 py-12">No KPIs defined for this department. Please create KRAs and KPIs first.</p>
        ) : (
          <div className="space-y-6">
            {/* Month and Year Selection */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="review-month">Review Month *</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger id="review-month" data-testid="review-month-select">
                    <SelectValue placeholder="Select Month" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="review-year">Review Year *</Label>
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger id="review-year" data-testid="review-year-select">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* KPI Reviews with KRAs */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-slate-900">KRA & KPI Performance Review</h3>
              {kras.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No KRAs/KPIs defined for this department yet.</p>
              ) : (
                kras.map((kra) => {
                  const kraKpis = kpis.filter((kpi) => kpi.kra_id === kra.id);
                  if (kraKpis.length === 0) return null;
                  
                  return (
                    <Card key={kra.id} className="p-6 bg-blue-50 border-2 border-blue-200">
                      <h4 className="text-lg font-bold text-slate-900 mb-4">{kra.title}</h4>
                      {kra.description && (
                        <p className="text-sm text-slate-600 mb-4">{kra.description}</p>
                      )}
                      
                      <div className="space-y-4">
                        {kraKpis.map((kpi) => {
                          const review = kpiReviews.find(r => r.kpi_id === kpi.id) || {};
                          return (
                            <Card key={kpi.id} className="p-4 bg-white border-2 border-slate-200" data-testid="kpi-review-card">
                              <div className="space-y-3">
                                <div className="flex justify-between items-start">
                                  <div className="flex-1">
                                    <h5 className="font-bold text-slate-900">{kpi.title}</h5>
                                    {kpi.description && (
                                      <p className="text-sm text-slate-600 mt-1">{kpi.description}</p>
                                    )}
                                    <p className="text-sm text-slate-600 mt-1">
                                      Weightage: <span className="font-bold">{kpi.weightage}%</span>
                                    </p>
                                  </div>
                                </div>
                                <div className="grid md:grid-cols-2 gap-4">
                                  <div>
                                    <Label>Achieved Score (out of {kpi.weightage}%) *</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max={kpi.weightage}
                                      step="0.1"
                                      placeholder={`0-${kpi.weightage}`}
                                      value={review.achieved_percentage || ''}
                                      onChange={(e) => updateKPIReview(kpi.id, 'achieved_percentage', e.target.value)}
                                      data-testid="achieved-percentage-input"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">Enter the score earned out of {kpi.weightage}%</p>
                                  </div>
                                  <div>
                                    <Label>Feedback</Label>
                                    <Input
                                      placeholder="Provide feedback..."
                                      value={review.feedback || ''}
                                      onChange={(e) => updateKPIReview(kpi.id, 'feedback', e.target.value)}
                                      data-testid="kpi-feedback-input"
                                    />
                                  </div>
                                </div>

                                {/* File Upload Section */}
                                <div className="mt-4 border-t border-slate-200 pt-4">
                                  <Label className="text-sm font-semibold text-slate-700">Attach Evidence (Optional)</Label>
                                  <p className="text-xs text-slate-500 mb-2">Upload supporting documents (PDF, XLS, XLSX, JPG, PNG, GIF - Max 5MB)</p>
                                  
                                  {!review.evidence_file ? (
                                    <div className="space-y-2">
                                      {/* File Upload Option */}
                                      <div className="flex items-center gap-2">
                                        <Input
                                          type="file"
                                          accept=".pdf,.xls,.xlsx,.jpg,.jpeg,.png,.gif"
                                          onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                              handleFileUpload(kpi.id, file);
                                            }
                                          }}
                                          ref={(el) => {
                                            if (el) fileInputRefs.current[kpi.id] = el;
                                          }}
                                          disabled={review.evidence_uploading}
                                          className="flex-1"
                                          data-testid="evidence-file-input"
                                        />
                                        {review.evidence_uploading && (
                                          <span className="text-sm text-blue-600">Uploading...</span>
                                        )}
                                      </div>
                                      
                                      {/* Camera Capture Option */}
                                      <div className="flex items-center gap-2">
                                        <div className="flex-1 flex items-center gap-2 text-xs text-slate-500">
                                          <div className="h-px flex-1 bg-slate-200"></div>
                                          <span>OR</span>
                                          <div className="h-px flex-1 bg-slate-200"></div>
                                        </div>
                                      </div>
                                      <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => handleCameraCapture(kpi.id)}
                                        disabled={review.evidence_uploading}
                                        className="w-full border-2 border-blue-200 hover:border-blue-400 hover:bg-blue-50"
                                        data-testid="camera-capture-button"
                                      >
                                        <Camera className="w-4 h-4 mr-2" />
                                        Click Photo
                                      </Button>
                                    </div>
                                  ) : (
                                    <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                                      <div className="flex items-center gap-2">
                                        <FileCheck className="w-5 h-5 text-green-600" />
                                        <div>
                                          <p className="text-sm font-semibold text-green-900">{review.evidence_file.original_filename}</p>
                                          <p className="text-xs text-green-700">
                                            {(review.evidence_file.size / 1024).toFixed(2)} KB - {review.evidence_file.file_type.toUpperCase()}
                                          </p>
                                        </div>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removeEvidence(kpi.id)}
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </Card>
                          );
                        })}
                      </div>
                    </Card>
                  );
                })
              )}
            </div>

            {/* Overall Feedback */}
            <div>
              <Label htmlFor="overall-feedback">Overall Feedback</Label>
              <Textarea
                id="overall-feedback"
                placeholder="Provide overall feedback for the department's performance this month..."
                value={overallFeedback}
                onChange={(e) => setOverallFeedback(e.target.value)}
                rows={4}
                data-testid="overall-feedback-input"
              />
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmitReview}
              disabled={loading}
              className="w-full md:w-auto bg-green-600 hover:bg-green-700 text-white font-semibold py-6 px-8"
              data-testid="submit-review-button"
            >
              <Save className="w-5 h-5 mr-2" />
              Submit Review
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
}